import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '@/utils/api';

export interface DailySummary {
  id: string;
  summaryDate: string;
  sessionsCompleted: number;
  cardsCompleted: number;
  patternsPracticed: number;
  totalMinutes: number;
  targetMinutes: number;
  isStreakDay: boolean;
  streakCount: number;
}

export interface StreakInfo {
  currentStreak: number;
  longestStreak: number;
  totalDays: number;
  thisWeekDays: number;
  freezesAvailable: number;
  freezesUsedThisMonth: number;
}

export interface WeeklyProgress {
  days: {
    date: string;
    isComplete: boolean;
    isFreeze: boolean;
    minutes: number;
  }[];
  totalMinutes: number;
  avgMinutes: number;
}

export interface ProgressDelta {
  period: 'week' | 'month' | 'quarter';
  current: {
    sessions: number;
    minutes: number;
    patterns: number;
  };
  previous: {
    sessions: number;
    minutes: number;
    patterns: number;
  };
  change: {
    sessions: number;
    minutes: number;
    patterns: number;
  };
}

interface MomentumState {
  today: DailySummary | null;
  streak: StreakInfo | null;
  weeklyProgress: WeeklyProgress | null;
  progressDelta: ProgressDelta | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: MomentumState = {
  today: null,
  streak: null,
  weeklyProgress: null,
  progressDelta: null,
  isLoading: false,
  error: null,
};

export const fetchTodayMomentum = createAsyncThunk(
  'momentum/fetchToday',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/momentum/today');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch momentum'
      );
    }
  }
);

export const fetchStreakInfo = createAsyncThunk(
  'momentum/fetchStreak',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/momentum/streaks');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch streak info'
      );
    }
  }
);

export const fetchWeeklyProgress = createAsyncThunk(
  'momentum/fetchWeekly',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/momentum/week');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch weekly progress'
      );
    }
  }
);

export const fetchProgressDelta = createAsyncThunk(
  'momentum/fetchDelta',
  async (period: 'week' | 'month' | 'quarter' = 'week', { rejectWithValue }) => {
    try {
      const response = await api.get(`/momentum/delta/${period}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch progress delta'
      );
    }
  }
);

export const useStreakFreeze = createAsyncThunk(
  'momentum/useFreeze',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/momentum/freeze');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to use streak freeze'
      );
    }
  }
);

const momentumSlice = createSlice({
  name: 'momentum',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch today momentum
      .addCase(fetchTodayMomentum.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTodayMomentum.fulfilled, (state, action) => {
        state.isLoading = false;
        state.today = action.payload;
      })
      .addCase(fetchTodayMomentum.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch streak info
      .addCase(fetchStreakInfo.fulfilled, (state, action) => {
        state.streak = action.payload;
      })
      // Fetch weekly progress
      .addCase(fetchWeeklyProgress.fulfilled, (state, action) => {
        state.weeklyProgress = action.payload;
      })
      // Fetch progress delta
      .addCase(fetchProgressDelta.fulfilled, (state, action) => {
        state.progressDelta = action.payload;
      })
      // Use streak freeze
      .addCase(useStreakFreeze.fulfilled, (state, action) => {
        if (state.streak) {
          state.streak.freezesAvailable = action.payload.freezesRemaining;
          state.streak.freezesUsedThisMonth = action.payload.freezesUsedThisMonth;
        }
      });
  },
});

export const { clearError } = momentumSlice.actions;
export default momentumSlice.reducer;
